import { Button } from '@ads/components-react';
import { brandLinka } from '@ads/tokens';
import { ProductWithdrawalData } from '@linka/domain/dtos';
import { useTranslation } from '@linka/i18n';
import { formatDate } from '@linka/utils';
import { useNavigate } from 'react-router';
import { Bar, BarChart, CartesianGrid, LabelList, XAxis, YAxis } from 'recharts';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '../../../chart/chart';
import {
  ChartCard,
  ChartCardContent,
  ChartCardFooter,
  ChartCardHeader,
  ChartCardTitle,
  ExportChartTriggerButton,
} from '../components';
import { firstSegmentLabel, firstSegmentShape, hideZeroLabel } from './stacked-bar';

interface ProductsWithdrawnProps {
  data: ProductWithdrawalData[];
}

export const ProductsWithdrawn = ({ data }: ProductsWithdrawnProps) => {
  const navigate = useNavigate();
  const t = useTranslation();

  const chartConfig = {
    withdrawnCount: { label: t('withdrawn'), color: brandLinka.baseColor.brand['400'] },
    notWithdrawnCount: { label: t('not-withdrawn'), color: brandLinka.contextualColor.interactive.primary },
  };

  return (
    <ChartCard>
      <ChartCardHeader>
        <ChartCardTitle>{t('dashboard.charts.products-withdrawn')}</ChartCardTitle>
        <ExportChartTriggerButton type='OVERVIEW_PRODUCT_WITHDRAWAL' />
      </ChartCardHeader>
      {data.length === 0 ? (
        <ChartCardContent className='flex flex-1 items-center justify-center py-xl'>
          <span className='ts-paragraph-xs text-ctx-content-base'>{t('dashboard.charts.no-data')}</span>
        </ChartCardContent>
      ) : (
        <ChartCardContent className='flex flex-col'>
          <div className='flex items-center justify-end gap-sm pb-nano'>
            {Object.entries(chartConfig).map(([key, config]) => (
              <div key={key} className='flex items-center gap-quark'>
                <div className='h-3 w-3 shrink-0 rounded-sm' style={{ background: config.color }} />
                <span className='ts-caption-xxs text-ctx-content-base'>{config.label}</span>
              </div>
            ))}
          </div>

          <div className='flex flex-1'>
            <ChartContainer config={chartConfig} className='w-[60px]'>
              <BarChart responsive accessibilityLayer data={data} margin={{ top: 24 }} width={60}>
                <CartesianGrid vertical={false} />
                <Bar dataKey='withdrawnCount' stackId='a' fill='transparent' barSize={0} />
                <Bar dataKey='notWithdrawnCount' stackId='a' fill='transparent' barSize={0} />
                <YAxis tickLine={false} tickMargin={10} axisLine={false} width={60} />
              </BarChart>
            </ChartContainer>

            <div className='flex-1 overflow-x-auto pb-xs'>
              <ChartContainer config={chartConfig} className='w-full' dataLength={data.length}>
                <BarChart responsive accessibilityLayer data={data} margin={{ top: 24 }}>
                  <CartesianGrid vertical={false} />
                  <Bar
                    dataKey='withdrawnCount'
                    stackId='a'
                    fill='var(--color-withdrawnCount)'
                    shape={firstSegmentShape('notWithdrawnCount')}
                  >
                    <LabelList dataKey='withdrawnCount' content={firstSegmentLabel(data, 'notWithdrawnCount')} />
                  </Bar>
                  <Bar
                    dataKey='notWithdrawnCount'
                    stackId='a'
                    fill='var(--color-notWithdrawnCount)'
                    radius={[8, 8, 0, 0]}
                  >
                    <LabelList dataKey='notWithdrawnCount' position='top' formatter={hideZeroLabel} />
                  </Bar>
                  <XAxis
                    dataKey='date'
                    tickLine={false}
                    tickMargin={10}
                    axisLine={false}
                    tickFormatter={(val: string) => formatDate({ date: val, dateFormat: 'dd/MM/yyyy' }) ?? val}
                  />
                  <ChartTooltip
                    labelFormatter={(label) => formatDate({ date: label, dateFormat: 'dd/MM/yyyy' }) ?? label}
                    cursor={{ fill: brandLinka.contextualColor.interactive.secondary }}
                    content={<ChartTooltipContent />}
                  />
                </BarChart>
              </ChartContainer>
            </div>
          </div>
        </ChartCardContent>
      )}
      {data.length > 0 && (
        <ChartCardFooter>
          <div className='flex flex-wrap justify-end gap-nano py-nano *:!w-full sm:*:!w-auto'>
            <Button
              className='whitespace-nowrap !text-xxxs sm:!text-xxs'
              onClick={() => navigate('detailed-withdrawals')}
            >
              {t('dashboard.detailed.withdrawals-detail')}
            </Button>
          </div>
        </ChartCardFooter>
      )}
    </ChartCard>
  );
};
